import { FaCoins, FaCheck, FaLock } from 'react-icons/fa6';
import Thumb from './primitives/Thumb.jsx';
import { fmtDuration, fmtViews, videoCost } from '../../services/autoDub/cost.js';

/* A YouTube-style video card with a duration badge, optional Shorts pill,
   2-line title, views + age, and a per-language credit cost strip (PRD §7.2).
   `onPlay(v)` (optional) makes the thumbnail's play button open a preview.
   When `selectable`, a top-right checkbox reflects `selected` and `onToggle`
   adds/removes the video from the dub set (backfill / "Existing videos" only).
   In the edit picker, `locked` marks a video whose dub has already started — the
   checkbox is shown ticked but disabled — and `badge` ({label,color}) renders a
   small stage chip (e.g. "Dubbing", "Detected") for videos already in the run. */
export default function VideoCard({ v, onPlay, selectable = false, selected = true, onToggle, locked = false, badge = null }) {
	const dimmed = selectable && !selected;
	return (
		<div
			className={`bg-white rounded-2xl border shadow-small p-2.5 flex flex-col gap-2.5 transition ${selected ? 'border-primary/40 ring-1 ring-primary/30' : 'border-slate-200'} ${dimmed ? 'opacity-60' : ''}`}
		>
			<Thumb grad={v.grad} thumbnail={v.thumbnail} radius={10} dur={fmtDuration(v.dur)} onPlay={onPlay ? () => onPlay(v) : undefined}>
				{badge && (
					<div className="absolute top-2 left-2">
						<span className="text-[10.5px] font-bold text-white px-2 py-[3px] rounded-full shadow-[0_1px_4px_rgba(0,0,0,0.3)]" style={{ background: badge.color }}>{badge.label}</span>
					</div>
				)}
				{v.type === 'short' && (
					<div className="absolute right-2 bottom-2">
						<span className="text-[11px] font-semibold text-white px-2 py-[3px] rounded-full" style={{ background: 'rgba(255,0,51,.85)' }}>Shorts</span>
					</div>
				)}
				{selectable && (
					<button
						type="button"
						disabled={locked}
						onClick={(e) => { e.stopPropagation(); if (!locked) onToggle?.(); }}
						aria-label={locked ? 'Already in this automation — locked' : selected ? 'Deselect video' : 'Select video'}
						aria-pressed={selected}
						title={locked ? 'Already being dubbed — can\'t be removed' : undefined}
						className={`absolute top-2 right-2 w-6 h-6 rounded-md grid place-items-center transition shadow-[0_1px_6px_rgba(0,0,0,0.35)] ${locked ? 'bg-slate-500/90 text-white cursor-not-allowed' : selected ? 'bg-primary text-white' : 'bg-white/90 text-transparent ring-1 ring-black/10 hover:bg-white'}`}
					>
						{locked ? <FaLock size={10} /> : <FaCheck size={12} />}
					</button>
				)}
			</Thumb>
			<div className="px-1 pb-0.5 flex flex-col gap-2 flex-1">
				<div className="text-[13px] font-semibold text-slate-800 leading-snug line-clamp-2 min-h-[35px]">{v.title}</div>
				<div className="text-[12px] text-slate-500">{fmtViews(v.views)} views · {v.ago}</div>
				<div className="mt-auto flex items-center gap-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5">
					<FaCoins size={12} className="text-amber-500" />
					<span className="text-[12px] font-bold text-amber-700">≈ {videoCost(v).toLocaleString()}</span>
					<span className="text-[11.5px] text-amber-600">credits / language</span>
				</div>
			</div>
		</div>
	);
}
